import { useQuery } from '@tanstack/react-query';
import { useUnifiedAuth } from '@/hooks/useUnifiedAuth';
import { useAppSelector } from '@/store/hooks';
import { fetchCollects, type CollectsItem, type CollectsFilterInput } from '@/integrations/agreements/client';
import { attachDocumentFlags, fetchDocumentFlags } from '@/services/collectDocumentFlags';
import type { FilterState } from '@/hooks/useFilters';
import {
  clampDateRangeForCutoff,
  excludeCutoffItems,
  resolveEffectiveGeneratorIds,
} from '@/config/legacyResicleanCutoff';

/** Escopo do download em massa: Histórico (coletas realizadas) ou Próximas (planejadas). */
export type MtrBulkScope = 'historico' | 'proximas';

/** Materiais cujas coletas planejadas são ocultadas nas listagens (RCC e Óleo). */
export const EXCLUDE_PLANNED_MATERIAL_IDS = [4, 5, 26];

function toYYYYMMDD(d: Date): string {
  // Formata em horário local (evita o off-by-one que toISOString causa em fim de
  // dia para fusos negativos, ex.: domingo 23:59 BRT vira segunda em UTC).
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

/** Monta o filtro da API a partir dos filtros da interface (FiltersBar). */
export function buildCollectsFilter(
  filters: FilterState | undefined,
  profile: { is_internal?: boolean; generator_id?: number | null } | null,
  generatorsFromRedux: Array<{ id: string; external_id: string }>,
  establishmentsFromRedux: Array<{ id: string; external_id: string }>,
  page: number,
  pageSize: number,
  options?: {
    status?: string[] | null;
    excludeStatus?: string[] | null;
    orderDirection?: 'asc' | 'desc' | null;
    /** Janela de datas explícita (ex.: navegação de calendário). Sobrepõe filters.dateRange. */
    window?: { start: Date; end: Date } | null;
    /** Exclui coletas planejadas desses material_ids (ex.: RCC e Óleo). */
    excludePlannedMaterialIds?: number[] | null;
  }
): CollectsFilterInput {
  const rangeStart = options?.window?.start ?? filters?.dateRange?.start;
  const rangeEnd = options?.window?.end ?? filters?.dateRange?.end;
  const rawDateFrom = rangeStart ? toYYYYMMDD(rangeStart) : toYYYYMMDD(new Date());
  const rawDateTo = rangeEnd ? toYYYYMMDD(rangeEnd) : toYYYYMMDD(new Date());

  // Geradores: converter external_id para id numérico.
  //
  // Estabelecimento tem precedência: escolher um gerador já preenche a seleção
  // de estabelecimentos (FiltersBar), e os dois viajam como AND separados no
  // gateway (collects/service.ts). Mandar os dois faria "gerador A AND
  // estabelecimento de B" devolver zero quando o usuário troca de
  // estabelecimento — o filtro que ele acabou de mexer perderia para o que
  // ficou para trás.
  //
  // O filtro de Gerador vale para os dois perfis: o gateway intersecta o que
  // foi pedido com o escopo do usuário (collects/service.ts), então mandar o
  // gerador escolhido não amplia acesso — antes o valor era descartado para o
  // gerador externo e o filtro não fazia nada.
  const hasEstablishmentFilter = Boolean(filters?.establishments?.length);
  let generator_id: number[] | null = null;
  if (filters?.generators?.length && !hasEstablishmentFilter) {
    const generatorIds = filters.generators
      .map((externalId) => {
        const found = generatorsFromRedux.find(
          (g) => g.external_id === externalId || g.id === externalId
        );
        return found ? parseInt(found.id, 10) : null;
      })
      .filter((id): id is number => id !== null);
    generator_id = generatorIds.length > 0 ? generatorIds : null;
  }
  // Sem filtro explícito, não manda nada: o escopo do usuário externo vem do
  // token, resolvido no gateway (buildUserGeneratorFilter). Mandar
  // `profile.generator_id` aqui era inofensivo enquanto o gateway descartava o
  // valor, mas agora ele é intersectado com o escopo — e como esse id é o
  // gerador "principal" do perfil, não o das folhas em que a coleta é gravada,
  // a interseção zerava a lista inteira.

  // Materiais: converter string IDs para números
  const material_id: number[] | null =
    filters?.materials && filters.materials.length > 0
      ? filters.materials
          .map((id) => {
            const n = parseFloat(String(id));
            return Number.isNaN(n) ? null : n;
          })
          .filter((id): id is number => id !== null)
      : null;

  // Estados/UF: array de strings
  const uf: string[] | null =
    filters?.states && filters.states.length > 0 ? filters.states : null;

  // Estabelecimentos: converter external_id para id numérico
  const establishment_id: number[] | null =
    filters?.establishments && filters.establishments.length > 0
      ? filters.establishments
          .map((externalId) => {
            const found = establishmentsFromRedux.find(
              (e) => e.external_id === externalId || e.id === externalId
            );
            return found ? parseInt(found.id, 10) : null;
          })
          .filter((id): id is number => id !== null)
      : null;

  // MC-107: recorta o período com coletas retroativas fictícias (jan-jul/2026)
  // quando a consulta é inteiramente escopada a gerador(es) da carteira Resiclean
  // onboardados sem esses dados. Ver src/config/legacyResicleanCutoff.ts.
  const effectiveGeneratorIds = resolveEffectiveGeneratorIds(generator_id, profile?.generator_id ?? null);
  const { date_from, date_to } = clampDateRangeForCutoff(effectiveGeneratorIds, rawDateFrom, rawDateTo);

  const filter: CollectsFilterInput = {
    page,
    page_size: pageSize,
    date_from,
    date_to,
  };

  if (generator_id != null && generator_id.length > 0) filter.generator_id = generator_id;
  if (material_id != null && material_id.length > 0) filter.material_id = material_id;
  if (uf != null && uf.length > 0) filter.uf = uf;
  if (establishment_id != null && establishment_id.length > 0) filter.establishment_id = establishment_id;
  if (options?.status != null && options.status.length > 0) filter.status = options.status;
  if (options?.excludeStatus != null && options.excludeStatus.length > 0) filter.exclude_status = options.excludeStatus;
  if (options?.orderDirection) filter.order_direction = options.orderDirection;
  if (options?.excludePlannedMaterialIds != null && options.excludePlannedMaterialIds.length > 0)
    filter.exclude_planned_material_ids = options.excludePlannedMaterialIds;
  if (filters?.hasMtr === true) filter.has_mtr = true;
  else if (filters?.hasMtr === false) filter.has_mtr = false;

  return filter;
}

/** Formato esperado pela CollectsTable (compatível com o que vinha do Supabase). */
export interface CollectForTable {
  collect_id: string;
  establishment_id: string;
  establishment_name: string | null;
  planned_date: string;
  kind_of_material: string | null;
  /** Id do material — define a unidade do `weight` (kg, Lt ou un) pelo catálogo. */
  material_id: number | null;
  packaging_name: string | null;
  units: number | null;
  /** Realizado **na unidade do material** (kg, litros ou unidades) — ver `utils/materialUnit`. */
  weight: number | null;
  volume: number | null;
  hauler_name: string | null;
  has_collect_evidence: boolean | null;
  has_mtr: boolean | null;
  has_cdf: boolean | null;
  sla_status: string | null;
  mtr_link: string | null;
  city: string | null;
  state: string | null;
  street: string | null;
  number: string | null;
  neighborhood: string | null;
  zipcode: string | null;
  establishments?: { establishment_name: string };
}

const PAGE_SIZE = 100;

export function mapCollectToTable(item: CollectsItem): CollectForTable {
  return {
    collect_id: item.id,
    establishment_id: item.establishment?.document ?? '',
    establishment_name: item.establishment?.name ?? null,
    planned_date: item.planned_date ?? '',
    kind_of_material: item.material?.name ?? null,
    material_id: item.material?.id ?? null,
    packaging_name: item.packaging?.name ?? null,
    units: item.units ?? null,
    weight: item.weight ?? null,
    volume: item.volume ?? null,
    hauler_name: null,
    sla_status: item.status ?? null,
    has_collect_evidence: item.has_collect_evidence ?? null,
    has_mtr: item.has_mtr ?? null,
    has_cdf: item.has_cdf ?? null,
    mtr_link: null,
    city: item.establishment?.city ?? null,
    state: item.establishment?.state ?? null,
    street: item.establishment?.street ?? null,
    number: item.establishment?.number ?? null,
    neighborhood: item.establishment?.neighborhood ?? null,
    zipcode: item.establishment?.zipcode ?? null,
    establishments: item.establishment?.name
      ? { establishment_name: item.establishment.name }
      : undefined,
  };
}

/** Mapeia um item enxuto (CollectSummaryItem) para o shape do card de calendário. */
export function mapSummaryItemToTable(item: {
  id: string;
  planned_date: string;
  status: string;
  establishment_name?: string | null;
  city?: string | null;
  state?: string | null;
  material_name?: string | null;
  packaging_name?: string | null;
  street?: string | null;
  number?: string | null;
  neighborhood?: string | null;
  zipcode?: string | null;
}): CollectForTable {
  return {
    collect_id: item.id,
    establishment_id: '',
    establishment_name: item.establishment_name ?? null,
    planned_date: item.planned_date ?? '',
    kind_of_material: item.material_name ?? null,
    material_id: null,
    packaging_name: item.packaging_name ?? null,
    units: null,
    weight: null,
    volume: null,
    hauler_name: null,
    sla_status: item.status ?? null,
    has_collect_evidence: null,
    has_mtr: null,
    has_cdf: null,
    mtr_link: null,
    city: item.city ?? null,
    state: item.state ?? null,
    street: item.street ?? null,
    number: item.number ?? null,
    neighborhood: item.neighborhood ?? null,
    zipcode: item.zipcode ?? null,
    establishments: item.establishment_name
      ? { establishment_name: item.establishment_name }
      : undefined,
  };
}

export interface UseCollectsApiDataParams {
  filters?: FilterState;
  page?: number;
  pageSize?: number;
  /** Filtra por status (ex.: ['planned'] em Próximas Coletas). */
  status?: string[] | null;
  /** Exclui status específicos (ex.: ['planned'] no Histórico de Coletas). */
  excludeStatus?: string[] | null;
  /** Ordenação por planned_date. Default no back: 'desc'. */
  orderDirection?: 'asc' | 'desc' | null;
  /** Janela de datas explícita (ex.: navegação de calendário). Sobrepõe filters.dateRange. */
  window?: { start: Date; end: Date } | null;
  /** Exclui coletas planejadas desses material_ids (ex.: RCC e Óleo). */
  excludePlannedMaterialIds?: number[] | null;
  /** Permite desabilitar a query (ex.: aba inativa). */
  enabled?: boolean;
}

export interface CollectsPaginationMeta {
  page: number;
  last_page: number;
  page_size: number;
  count: number;
}

export interface UseCollectsApiDataResult {
  data: CollectForTable[];
  pagination: CollectsPaginationMeta;
}

export type CollectsScope = Pick<
  CollectsFilterInput,
  'generator_id' | 'establishment_id' | 'material_id' | 'uf'
>;

/** Extrai apenas o escopo (gerador/estabelecimento/material/UF) dos filtros da UI. */
export function useCollectsScope(filters?: FilterState) {
  const { profile } = useUnifiedAuth();
  const generatorsFromRedux = useAppSelector((s) => s.establishmentAccess.generators);
  const establishmentsFromRedux = useAppSelector((s) => s.establishmentAccess.establishments);
  const base = buildCollectsFilter(
    filters,
    profile,
    generatorsFromRedux,
    establishmentsFromRedux,
    1,
    1,
  );
  const scope: CollectsScope = {
    generator_id: base.generator_id ?? null,
    establishment_id: base.establishment_id ?? null,
    material_id: base.material_id ?? null,
    uf: base.uf ?? null,
  };
  return { profile, scope };
}

export function useCollectsApiData(params?: UseCollectsApiDataParams) {
  const { profile } = useUnifiedAuth();
  const generatorsFromRedux = useAppSelector((s) => s.establishmentAccess.generators);
  const establishmentsFromRedux = useAppSelector((s) => s.establishmentAccess.establishments);
  const {
    filters,
    page = 1,
    pageSize = PAGE_SIZE,
    status = null,
    excludeStatus = null,
    orderDirection = null,
    window = null,
    excludePlannedMaterialIds = null,
    enabled = true,
  } = params ?? {};

  const apiFilter = buildCollectsFilter(
    filters,
    profile,
    generatorsFromRedux,
    establishmentsFromRedux,
    page,
    pageSize,
    { status, excludeStatus, orderDirection, window, excludePlannedMaterialIds }
  );

  return useQuery({
    queryKey: ['collects-api', apiFilter],
    queryFn: async (): Promise<UseCollectsApiDataResult> => {
      const result = await fetchCollects(apiFilter);
      const rawItems = result?.data ?? [];
      // MC-107: segunda camada de defesa contra dado retroativo oculto — cobre
      // o caso de a consulta misturar geradores da carteira Resiclean com
      // outros (o recorte de intervalo em buildCollectsFilter não se aplica
      // nesse caso). O count/last_page do backend não sabe desse pós-filtro,
      // então pode ficar levemente impreciso quando há itens removidos.
      const filteredItems = excludeCutoffItems(
        rawItems,
        (item) => item.establishment?.generator_id,
        (item) => item.planned_date,
      );
      const mapped = filteredItems.map(mapCollectToTable);
      // MTR/CDF vêm do mtr-manager, por coleta; o agreements devolve nulo. Falha lá não derruba a lista.
      const data = attachDocumentFlags(mapped, await fetchDocumentFlags(mapped.map((item) => item.collect_id)));
      const pagination = result?.pagination;
      const removedCount = rawItems.length - filteredItems.length;

      return {
        data,
        pagination: {
          page: pagination?.page ?? page,
          last_page: pagination?.last_page ?? (data.length > 0 ? page : 0),
          page_size: pagination?.page_size ?? pageSize,
          count: Math.max(0, (pagination?.count ?? rawItems.length) - removedCount),
        },
      };
    },
    enabled: !!profile && enabled,
    staleTime: 2 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  });
}
